<template>
  <Dialog v-model:visible="visible" modal :draggable="false" :closable="false" style="min-width: 720px">
    <template #header>
      <h2>Change price</h2>
    </template>
    <Form
      v-slot="$form"
      style="display: flex; flex-direction: column; gap: 16px"
      :resolver="schema"
      @submit="onFormSubmit"
    >
      <div>
        <label>Current price:</label>
        <p>
          <b>{{ formatPrice(iap.pricing[0].value, iap.pricing[0].currency) }}</b>
        </p>
      </div>
      <FormField name="pricingTemplateId" style="display: flex; flex-direction: column">
        <label for="pricingTemplateId">Change price:</label>
        <Select
          input-id="pricingTemplateId"
          placeholder="Select a pricing template"
          :options="
            [...systemPricingTemplates, ...appPricingTemplates].sort(
              (a, b) => a.defaultPriceAmountMicros - b.defaultPriceAmountMicros
            )
          "
          option-label="name"
          option-value="id"
          fluid
          filter
        />
        <Message v-if="$form.pricingTemplateId?.invalid" severity="error" size="small" variant="simple">
          {{ $form.pricingTemplateId.error.message }}
        </Message>
      </FormField>
      <div :class="$style.formButtons">
        <Button type="submit" label="Change price" :loading="loading" />
        <Button type="button" label="Cancel" severity="secondary" @click="onCancel()" />
      </div>
    </Form>
  </Dialog>
</template>

<script setup>
import { Button, Dialog, Message, Select } from "primevue";
import { Form, FormField } from "@primevue/forms";
import { yupResolver } from "@primevue/forms/resolvers/yup";
import { defineEmits, defineModel, onMounted, ref } from "vue";
import usePricingTemplates from "../hooks/usePricingTemplates";
import useToast from "@/hooks/useToastNotification";
import { useRoute } from "vue-router";
import * as yup from "yup";
import { formatPrice } from "@/utils/formatPrice";

const {
  appPricingTemplates,
  systemPricingTemplates,
  getSystemPriceTemplates,
  getAppPriceTemplates,
  linkPricingTemplateToIAP,
} = usePricingTemplates();
const { toastSuccess } = useToast();
const route = useRoute();
const iap = defineModel();
const emit = defineEmits(["submit"]);

const visible = defineModel("visible");
const loading = ref(false);
const gameId = ref(route.params.gameId);
const schema = ref(
  yupResolver(
    yup.object({
      pricingTemplateId: yup.string().required("Price is required"),
    })
  )
);
const onFormSubmit = async (form) => {
  if (form.valid) {
    loading.value = true;
    const result = await linkPricingTemplateToIAP(iap.value.appId, form.values.pricingTemplateId, iap.value.id);
    loading.value = false;
    if (result) {
      toastSuccess({
        detail: `Successfully updated price to iap "${iap.value.name}" using ${
          [...systemPricingTemplates.value, ...appPricingTemplates.value].find(
            (template) => template.id === form.values.pricingTemplateId
          ).name
        }.`,
        summary: "Success",
        life: 5000,
      });
      visible.value = false;
      emit("submit", result);
    }
  }
};

onMounted(async () => {
  await getSystemPriceTemplates(gameId.value);
  await getAppPriceTemplates(gameId.value);
});

function onCancel() {
  visible.value = false;
}
</script>

<style module>
.formButtons {
  display: flex;
  gap: 8px;
  flex-direction: row-reverse;
}
</style>
